import { useMediaQuery, useTheme } from '@mui/material'
import { ApiTableColumn } from '@e3dc-react/shell/Elements/ApiView/ApiView'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { makeStyles } from 'tss-react/mui'
import { getInstallationTableColumns } from '../utils/installation-table-columns'
import { Installation } from '../models/installation.model'
import { isFlowTheme } from 'src/Libs/utils'

interface InstallationTableColumnsHook {
  tableColumns: ApiTableColumn<Installation>[]
}

const useStyles = makeStyles()(theme => ({
  productTypContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    gap: theme.spacing(1),
  },
  productImg: {
    height: 35,
  },
  installationInfoContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  installationInfoText: {
    display: 'flex',
    flexDirection: 'column',
  },
  installationCreatedByContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}))

/*
 * This Hook provides table columns for an installation table based on media breakpoints
 * Eg.:
 * const { tableColumns } = usInstallationTableColumns
 *
 *  <ApiView<Installation>
      tableColumns={tableColumns}
      {...props}
    />
 */

interface InstallationTableColumnsParams {
  showOptionsMenu: boolean
  showStatus: boolean
  noAccess?: boolean
  showFullActionsMenu?: boolean
  isPlannedView?: boolean
}

export const useInstallationTableColumns = ({
  showOptionsMenu,
  showStatus,
  noAccess,
  showFullActionsMenu,
  isPlannedView,
}: InstallationTableColumnsParams): InstallationTableColumnsHook => {
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const downMd = useMediaQuery(theme.breakpoints.down('md'))
  const downLg = useMediaQuery(theme.breakpoints.down('lg'))
  const { t } = useCustomTranslation()
  const { classes } = useStyles()

  const { tableColumnsAll, tableColumnsLg, tableColumnsMd, tableColumnsSm } = getInstallationTableColumns({
    t,
    productTypContainerClass: classes.productTypContainer,
    productImgClass: classes.productImg,
    installationInfoContainerClass: classes.installationInfoContainer,
    installationInfoTextClass: classes.installationInfoText,
    installationCreatedByContainerClass: classes.installationCreatedByContainer,
    showOptionsMenu,
    showStatus,
    noAccess,
    showFullActionsMenu,
    isFlowTheme,
    isPlannedView,
  })

  const getTableColumns = (): ApiTableColumn<Installation>[] => {
    if (downSm) return tableColumnsSm
    if (downMd) return tableColumnsMd
    if (downLg) return tableColumnsLg
    return tableColumnsAll
  } // eslint-disable-line

  const tableColumns = getTableColumns()

  return { tableColumns }
}
